import React, { useState, useEffect } from "react";
import { Copy, MapPin, Phone, Shield, AlertTriangle } from "lucide-react";
import LoadingSpinner from "../../components/LoadingSpinner";
import ErrorMessage from "../../components/ErrorMessage";
import { useLanguage } from "../../contexts/LanguageContext";
import { fetchAnalyticsQuality } from "./analyticsApi";
import type { AnalyticsQualityResponse } from "../../types/analytics";

const QualityTab: React.FC = () => {
  const { t } = useLanguage();
  const [data, setData] = useState<AnalyticsQualityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchAnalyticsQuality()
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || t("analytics.error"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [t]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }
  if (error) {
    return <ErrorMessage message={error} onRetry={() => setError(null)} />;
  }
  if (!data) {
    return <p className="text-gray-500 text-center py-8">{t("analytics.noData")}</p>;
  }

  const cards = [
    { key: "duplicates", label: t("analytics.qualityDuplicates"), value: data.duplicates.length, icon: Copy },
    { key: "missingAddress", label: t("analytics.qualityMissingAddress"), value: data.missingAddressCount, icon: MapPin },
    { key: "invalidPhone", label: t("analytics.qualityInvalidPhone"), value: data.invalidPhoneCount, icon: Phone },
    { key: "bexLowConfidence", label: t("analytics.qualityBexLowConfidence"), value: data.bexLowConfidenceCount, icon: Shield },
    { key: "problematic", label: t("analytics.qualityProblematic"), value: data.problematicCount, icon: AlertTriangle },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {cards.map(({ key, label, value, icon: Icon }) => (
          <div key={key} className="bg-white rounded-lg shadow p-4 flex items-center gap-3">
            <Icon className="h-8 w-8 text-[#264a3e] flex-shrink-0" />
            <div>
              <p className="text-sm text-gray-500">{label}</p>
              <p className="text-xl font-semibold text-gray-900">{value}</p>
            </div>
          </div>
        ))}
      </div>

      {data.duplicates.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <h3 className="px-4 py-3 bg-gray-50 text-sm font-medium text-gray-700 border-b border-gray-200">{t("analytics.qualityDuplicates")} (top 50)</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">uniqueKey</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Count</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.duplicates.map((d) => (
                  <tr key={d.uniqueKey} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap font-mono truncate max-w-md">{d.uniqueKey}</td>
                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{d.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data.problematicBreakdown.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <h3 className="px-4 py-3 bg-gray-50 text-sm font-medium text-gray-700 border-b border-gray-200">{t("analytics.qualityProblematicBreakdown")}</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Reason</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Count</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.problematicBreakdown.map((r) => (
                  <tr key={r.reason} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{r.reason || "(empty)"}</td>
                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{r.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default QualityTab;
